import DropdownCommon, {
  DropdownItemStyled,
} from 'components/common/DropdownCommon';
import {
  ADD_TRANSACTION_PAYOUT_METHOD_NAME,
  ADD_TRANSACTION_RECEIVER_GET_NAME,
  RESET_BACKDROP_STORE,
} from 'constants';
import { ROUNDING_DELAY } from 'features/calculatorRounding/const';
import useCalculatorRounding from 'features/calculatorRounding/hooks/useCalculatorRounding';
import useCalculatorRoundingStore from 'features/calculatorRounding/hooks/useCalculatorRoundingStore';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import { SERVICE_ALERTS_TYPE } from 'hooks/service-alerts/model/ServiceAlertsModel';
import useCheckServiceAlerts from 'hooks/service-alerts/useCheckServiceAlerts';
import useServiceAlertsStore from 'hooks/service-alerts/useServiceAlertsStore';
import useDebounceCallback from 'hooks/useDebounceCallback';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useStore from 'hooks/useStore';
import { delay, isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { SET_SHOW_BACKDROP } from 'store/action';
import styled from 'styled-components';

export const ADD_TRANSACTION_CURRENCY_NAME = 'add-transaction-currency-field';

const CustomCurrencyDropdown = ({
  sendName,
  receiverName,
  countryConfig,
  maxSendLimit,
  receiveLimits,
  currentRateValue,
  setCurrentRateValue,
  information,
  setInformation,
  validation,
  setValidation,
  currentCurrencySelected,
  currencyProp,
  rateProp,
  isDisabled,
  handleSendAmountOnChange,
  handleGetReceiverAmountValidation,
  isFetching,
  setFetching,
  setOpenDiscountImportantPopup,
}) => {
  const { state, dispatch } = useStore();
  const { addTransactionInformation } = state || {};
  const { paymentMethod } = addTransactionInformation || {};
  const { isDesktop } = useDetectDevice();

  const [isShowDropdown, setShowDropdown] = useState(false);

  const { discountApplied } = paymentMethod || {};

  const dmCode =
    addTransactionInformation?.receiverPayoutMethod[
      ADD_TRANSACTION_PAYOUT_METHOD_NAME
    ];
  const countryCode = countryConfig?.countryCode;

  const { fetchCurrentFee, fetchRate } = useCalculatorRounding();
  const { setRateFetching } = useCalculatorRoundingStore();

  const { setReceivingAmount } = useFormAddTransactionStore();

  const {
    serviceAlertsByCountryCode,
    setServiceAlertsByCurrencyCode1,
    setServiceAlertsByCurrencyCode2,
    resetServiceAlertsByRangeOfCurrencyCode1,
    resetServiceAlertsByRangeOfCurrencyCode2,
  } = useServiceAlertsStore();
  const {
    serviceAlertsByCountryCodeData,
    serviceAlertsByCurrencyCode1,
    serviceAlertsByCurrencyCode2,
  } = serviceAlertsByCountryCode || {};
  const {
    checkByCurrencyCode,
    checkByPayoutMethodItem,
    checkByPayoutMethodItemChild,
  } = useCheckServiceAlerts();
  const {
    getServiceAlertsByCurrencyCode,
    setValidationOfServiceAlertsByCurrencyCode,
  } = checkByCurrencyCode();
  const { getServiceAlertsByPayoutMethodItem } = checkByPayoutMethodItem || {};
  const { getServiceAlertsByPayoutMethodItemChild } =
    checkByPayoutMethodItemChild || {};
  const serviceAlertsByCurrencyCode =
    currencyProp === 'currentCurrencySelected'
      ? serviceAlertsByCurrencyCode1
      : serviceAlertsByCurrencyCode2;
  const resetServiceAlertsByRangeOfCurrencyCode =
    currencyProp === 'currentCurrencySelected'
      ? resetServiceAlertsByRangeOfCurrencyCode1
      : resetServiceAlertsByRangeOfCurrencyCode2;
  const setServiceAlertsByCurrencyCode =
    currencyProp === 'currentCurrencySelected'
      ? setServiceAlertsByCurrencyCode1
      : setServiceAlertsByCurrencyCode2;
  const { type } = serviceAlertsByCurrencyCode || {};

  const serviceAlertsByPayoutMethod = serviceAlertsByCountryCodeData?.[dmCode];
  const serviceAlertsByPayoutMethodItem = getServiceAlertsByPayoutMethodItem({
    payoutMethod: dmCode || '',
    serviceAlertsByPayoutMethod,
  });
  const serviceAlertsByPayoutMethodItemChild =
    getServiceAlertsByPayoutMethodItemChild({
      payoutMethod: dmCode || '',
      serviceAlertsByPayoutMethodItem,
    });
  const currentServiceAlertsByCurrencyCode = getServiceAlertsByCurrencyCode({
    currencyCode: currentCurrencySelected?.currencyCode,
    serviceAlertsByCountryCode,
    serviceAlertsByPayoutMethod,
    serviceAlertsByPayoutMethodItem,
    serviceAlertsByPayoutMethodItemChild,
  });

  useEffect(() => {
    const sendAmount = Number(information[sendName] || 0) || '';
    const receiverAmount = Number(information[receiverName] || 0) || '';
    const rate = information[rateProp];
    const fee = information?.feeAmount;
    if (
      sendAmount &&
      receiverAmount &&
      !isEmpty(rate) &&
      !isEmpty(currentCurrencySelected) &&
      !isEmpty(fee)
    ) {
      setReceivingAmount({
        [sendName]: sendAmount,
        [receiverName]: receiverAmount,
        [currencyProp]: currentCurrencySelected,
      });
    }
  }, [information, currentCurrencySelected]);

  useEffect(() => {
    if (!isEmpty(currentServiceAlertsByCurrencyCode)) {
      setValidationOfServiceAlertsByCurrencyCode({
        currencyCode: currentCurrencySelected?.currencyCode,
        type,
        callback: (msg) => {
          setValidation({
            ...validation,
            [receiverName]: msg,
          });
          resetServiceAlertsByRangeOfCurrencyCode();
        },
      });
      return;
    }
    if (type === SERVICE_ALERTS_TYPE.DISABLE) {
      setServiceAlertsByCurrencyCode(null);
    }
  }, [currentServiceAlertsByCurrencyCode, currentCurrencySelected]);

  useEffect(() => {
    return () => {
      dispatch({ type: SET_SHOW_BACKDROP, payload: RESET_BACKDROP_STORE });
    };
  }, []);

  const isOnlyReceiverCurrency = receiveLimits?.length === 1;

  const { debounce: handleFetchCurrentFee } = useDebounceCallback(
    (information, setInformation, countryCode, dmCode) => {
      fetchCurrentFee(
        information,
        setInformation,
        countryCode,
        dmCode,
        maxSendLimit,
        () => setFetching(false),
        false,
        false,
      );

      setValidationOfServiceAlertsByCurrencyCode({
        currencyCode: currentCurrencySelected?.currencyCode,
        type,
        callback: (msg) => {
          setValidation({
            ...validation,
            [receiverName]: msg,
          });

          resetServiceAlertsByRangeOfCurrencyCode();
        },
      });
    },
    0,
  );

  useEffect(() => {
    const sendAmount = Number(information[sendName] || 0) || '';
    const receiverAmount = Number(information[receiverName] || 0) || '';

    if (
      ((sendAmount && !receiverAmount) || receiverAmount) &&
      !isDisabled &&
      Boolean(currentRateValue)
    ) {
      const [sendValue, receiverValue] = handleSendAmountOnChange(
        sendAmount,
        true,
      );

      const newInformation = {
        ...information,
        [sendName]: sendValue,
        [receiverName]: receiverValue,
      };

      let newValidation = {
        ...validation,
        [receiverName]: '',
      };

      newValidation = handleGetReceiverAmountValidation(
        newInformation,
        newValidation,
      );
      setValidation(newValidation);

      setFetching(true);
      handleFetchCurrentFee(
        newInformation,
        setInformation,
        countryCode,
        dmCode,
      );
    }
  }, [currentRateValue, isDisabled]);

  const handleCurrencySelected = async (currency) => {
    if (isFetching) return;

    // Only mark fetching (which locks the dropdown) when there is an amount to
    // recompute. With empty amounts a currency change fetches a rate but runs no
    // fee/amount computation, so blocking the dropdown during that async fetch
    // would wrongly stop an immediate re-open. See handleDropdownOnToggle.
    const hasAmount =
      Boolean(Number(information[sendName] || 0)) ||
      Boolean(Number(information[receiverName] || 0));
    if (
      currency?.currencyCode !== currentCurrencySelected?.currencyCode &&
      hasAmount
    ) {
      setFetching(true);
    }

    const newInformation = {
      ...information,
      [currencyProp]: currency,
    };
    setInformation(newInformation);

    const newValidation = {
      ...validation,
      [receiverName]: '',
    };
    setValidation(newValidation);

    fetchRate({
      countryCode,
      currency,
      setRate: setCurrentRateValue,
      rateProp,
      currencyProp,
      information,
      setInformation,
      callback: Number(information[receiverName] || 0)
        ? () => {
            delay(setRateFetching, ROUNDING_DELAY);
          }
        : () => {
            setFetching(false);
            delay(setRateFetching, ROUNDING_DELAY);
          },
    });

    dispatch({ type: SET_SHOW_BACKDROP, payload: RESET_BACKDROP_STORE });
  };

  const remainingCurrencies = () => {
    if (!information['isAddAnotherReceivingCurrency']) {
      return receiveLimits;
    }

    return receiverName === ADD_TRANSACTION_RECEIVER_GET_NAME
      ? receiveLimits.filter(
          (r) =>
            r?.currencyCode !==
            information['currentCurrencySelectedAnother']?.currencyCode,
        )
      : receiveLimits.filter(
          (r) =>
            r?.currencyCode !==
            information['currentCurrencySelected']?.currencyCode,
        );
  };

  // Track react-bootstrap's requested open state (nextShow) rather than flipping
  // our own boolean. The RB Dropdown is uncontrolled while our custom menu
  // renders on isShowDropdown, so toggling !isShowDropdown off a stale closure
  // let the two states diverge under rapid/double clicks (RB emits multiple
  // onToggle calls per interaction), leaving the menu stuck hidden. Mirroring
  // nextShow keeps our state in lockstep with RB so open/close can't desync.
  const handleDropdownOnToggle = (nextShow) => {
    if (isDisabled) {
      setShowDropdown(false);
      return;
    }
    if (isOnlyReceiverCurrency) {
      setShowDropdown(false);
      return;
    }
    if (isFetching) {
      setShowDropdown(false);
      return;
    }

    if (discountApplied && setOpenDiscountImportantPopup) {
      setOpenDiscountImportantPopup(true);
    }

    setShowDropdown(Boolean(nextShow));
  };

  const renderDropdownMenuChildren = () => {
    return (
      <>
        {remainingCurrencies()?.map((currency, index) => (
          <DropdownItemStyled
            key={index}
            eventKey={index}
            onClick={() => handleCurrencySelected(currency)}
            style={{
              borderRadius: '8px',
              justifyContent: 'center',
            }}
          >
            {currency.currencyCode}
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  const getStyleDropdown = () => {
    const common = { border: 'none' };
    if (!isDesktop)
      return {
        ...common,
      };
    return {
      ...common,
    };
  };
  const getStyleDropdownToggle = () => {
    const common = {
      textTransform: 'initial',
      paddingRight: isOnlyReceiverCurrency ? '10px' : '40px',
      fontWeight: 600,
    };
    if (!isDesktop)
      return {
        ...common,
      };
    return {
      ...common,
    };
  };
  const getStyleDropdownMenu = () => {
    const common = {
      borderRadius: '8px',
      background: '#fff',
      minWidth: '80px',
    };
    if (!isDesktop)
      return {
        ...common,
      };
    return {
      ...common,
    };
  };

  return (
    <CurrencyDropdownStyled>
      <DropdownCommon
        value={currentCurrencySelected?.currencyCode}
        isShowDropdown={isShowDropdown}
        isHideArrowDownIcon={isOnlyReceiverCurrency}
        onToggle={handleDropdownOnToggle}
        isDisabled={isDisabled}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
        styleDropdown={getStyleDropdown()}
        styleDropdownToggle={getStyleDropdownToggle()}
        styleDropdownMenu={getStyleDropdownMenu()}
      />
    </CurrencyDropdownStyled>
  );
};

const CurrencyDropdownStyled = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 27px;
  right: 0px;
`;

export default CustomCurrencyDropdown;
